import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../api';
import { PROJECT_STATUSES, normalizeProjectStatus } from '../utils/projectStatus';
import usePersistedFilters from '../hooks/usePersistedFilters';
import ProjectsFilterBar from '../components/ProjectsFilterBar';

// ── Helpers ────────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const fmtHrs = (n) => (Number(n) || 0).toFixed(1);
const pad2 = (n) => String(n).padStart(2, '0');
const brutoOf = (p) => (p.totalBruto === null || p.totalBruto === undefined ? p.totalHours : p.totalBruto);

const STATUS_CLASS = {
  in_progress: 'bg-state-progress-bg text-state-progress-fg',
  in_review: 'bg-state-review-bg text-state-review-fg',
  finished: 'bg-state-done-bg text-state-done-fg',
  u_izradi: 'bg-state-progress-bg text-state-progress-fg',
  na_reviziji: 'bg-state-review-bg text-state-review-fg',
  zavrseno: 'bg-state-done-bg text-state-done-fg',
};

const iconProps = {
  width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
};

/** Parse any reasonable date string → { year, month (1-12), day } or null */
const parseDate = (str) => {
  if (!str) return null;
  // ISO: 2025-08-07T... or 2025-08-07
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return {
      year: parseInt(iso[1], 10),
      month: parseInt(iso[2], 10),
      day: parseInt(iso[3], 10),
    };
  }
  // DD/MM/YYYY
  const dmy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmy) {
    return {
      year: parseInt(dmy[3], 10),
      month: parseInt(dmy[2], 10),
      day: parseInt(dmy[1], 10),
    };
  }
  return null;
};

const getDateSortValue = (str) => {
  const parsed = parseDate(str);
  if (!parsed) return Number.NEGATIVE_INFINITY;
  return Date.UTC(parsed.year, parsed.month - 1, parsed.day ?? 1);
};

// Croatian collation keeps diacritics in their expected alphabetic positions,
// while `numeric` makes names such as "Projekt 2" sort before "Projekt 10".
const croatianCollator = new Intl.Collator('hr', {
  numeric: true,
  sensitivity: 'base',
});

/** Format a raw date string → "August 2025" */
const formatMonthYear = (str) => {
  const d = parseDate(str);
  if (!d) return '—';
  return `${MONTH_NAMES[d.month - 1]} ${d.year}`;
};

const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return <span aria-hidden="true" className="ml-1 text-faint text-xs">↕</span>;
  return (
    <span aria-hidden="true" className="ml-1 text-accent text-sm">
      {sortDir === 'asc' ? '↑' : '↓'}
    </span>
  );
};

const CURRENT_YEAR = new Date().getFullYear();

// ── Component ──────────────────────────────────────────────────────────────────

const ProjectsPage = ({ loggedInUser }) => {
  const [projects, setProjects]   = useState([]);
  const [editing, setEditing]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [clients, setClients]     = useState([]);
  const [featured, setFeatured]   = useState(0);

  // Search is page-local; client, department, year and month are persisted per user.
  // Year/month are applied by the server (re-fetch), the rest filters client-side.
  const [search, setSearch] = useState('');
  const { prefs, setPref, loaded } = usePersistedFilters(loggedInUser);
  const filterClient = prefs.projectsFilterClient;
  const filterDept   = prefs.projectsFilterDept;
  const filterYear   = prefs.projectsFilterYear;   // '' = all years
  const filterMonth  = prefs.projectsFilterMonth;  // '' = all months
  const DEFAULT_YEAR = String(CURRENT_YEAR);
  const [availableYears, setAvailableYears] = useState(null); // null until the unfiltered list has been read

  // Sorting — default: most recent first
  const [sortCol, setSortCol] = useState('dateCreated');
  const [sortDir, setSortDir] = useState('desc');

  // Add Project modal
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', client_id: '' });
  const [adding, setAdding]   = useState(false);

  // Latest period, so refreshes after edit/delete/add keep the current view.
  const periodRef = useRef({ year: filterYear, month: filterMonth });
  periodRef.current = { year: filterYear, month: filterMonth };
  const requestId = useRef(0);

  useEffect(() => {
    fetchClients();
    // Year options come from the unfiltered list (the period-scoped list only holds that period's projects).
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/projects`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setAvailableYears([...new Set(
          (Array.isArray(data) ? data : []).map(p => parseDate(p.dateCreated)?.year).filter(Boolean)
        )]);
      } catch { setAvailableYears([]); }
    })();
  }, []);

  // Re-fetch when the period changes (debounced); wait for the saved filters so we do not fetch twice.
  const firstFetch = useRef(true);
  useEffect(() => {
    if (!loaded) return undefined;
    const delay = firstFetch.current ? 0 : 300;
    firstFetch.current = false;
    const t = setTimeout(() => fetchProjects(), delay);
    return () => clearTimeout(t);
  }, [loaded, filterYear, filterMonth]);

  const fetchProjects = async () => {
    const { year, month } = periodRef.current;
    const id = ++requestId.current;
    setLoading(true);
    try {
      const qs = year ? `?year=${encodeURIComponent(year)}${month ? `&month=${encodeURIComponent(month)}` : ''}` : '';
      const res = await fetch(`${API_BASE_URL}/api/projects${qs}`);
      if (!res.ok) throw new Error('Failed to fetch projects');
      const data = await res.json();
      if (id !== requestId.current) return; // a newer request superseded this one
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      if (id !== requestId.current) return;
      toast.error('Failed to fetch projects: ' + (err.message || err));
    }
    if (id === requestId.current) setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/clients`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  // ── Sort ──────────────────────────────────────────────────────────────────────
  // Project status (visible to requesters in their portal). Admins and designers may change it.
  const handleStatusChange = async (proj, status) => {
    const previous = proj.status;
    const previousAuto = proj.status_auto;
    setProjects(list => list.map(p => (p.id === proj.id ? { ...p, status, status_auto: false } : p)));
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${proj.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('failed');
    } catch {
      setProjects(list => list.map(p => (p.id === proj.id ? { ...p, status: previous, status_auto: previousAuto } : p)));
      toast.error('Failed to update project status');
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };

  // ── Derived filter options ────────────────────────────────────────────────────
  // The current selection is always an option, so a saved value never leaves a blank select.
  const uniqueClients = [...new Set([...projects.map(p => p.client), filterClient].filter(Boolean))].sort();
  const uniqueDepts   = [...new Set([...projects.map(p => p.department), filterDept].filter(Boolean))].sort();

  // Years that exist in the data, plus the current one (as before).
  const yearOptions = [...new Set([CURRENT_YEAR, ...(availableYears || [])])].sort((a, b) => b - a);

  // A saved year that no longer exists falls back to the current year (once the options are known).
  useEffect(() => {
    if (!loaded || availableYears === null || !filterYear) return;
    if (!yearOptions.includes(Number(filterYear))) setPref('projectsFilterYear', DEFAULT_YEAR);
  }, [loaded, availableYears, filterYear]);

  // ── Filter + sort ─────────────────────────────────────────────────────────────
  const filtered = projects
    .filter(p => {
      // text search
      const q = search.toLowerCase();
      if (q &&
        !p.project?.toLowerCase().includes(q) &&
        !p.client?.toLowerCase().includes(q) &&
        !p.requestedBy?.toLowerCase().includes(q)) return false;
      // dropdown filters (year and month are applied by the server)
      if (filterClient && p.client !== filterClient) return false;
      if (filterDept   && p.department !== filterDept) return false;
      return true;
    })
    .sort((a, b) => {
      let av = a[sortCol] ?? '';
      let bv = b[sortCol] ?? '';

      if (sortCol === 'totalHours' || sortCol === 'totalBruto') {
        av = Number(av);
        bv = Number(bv);
      } else if (sortCol === 'dateCreated') {
        av = getDateSortValue(av);
        bv = getDateSortValue(bv);
      } else {
        return croatianCollator.compare(String(av), String(bv)) * (sortDir === 'asc' ? 1 : -1);
      }

      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

  const maxHours = Math.max(...projects.map(p => Number(p.totalHours) || 0), 1);

  // Keep the featured index valid whenever the filtered list changes size.
  useEffect(() => { setFeatured(0); }, [filtered.length]);
  const featIdx = filtered.length ? Math.min(featured, filtered.length - 1) : 0;
  const featProj = filtered[featIdx];
  const peekProj = filtered.length > 1 ? filtered[(featIdx + 1) % filtered.length] : null;
  const stepFeatured = (delta) => setFeatured((featIdx + delta + filtered.length) % filtered.length);

  const sumNeto = filtered.reduce((sum, p) => sum + (Number(p.totalHours) || 0), 0);
  const sumBruto = filtered.reduce((sum, p) => sum + (Number(brutoOf(p)) || 0), 0);
  const hasPeriod = Boolean(filterYear);
  const sumNetoAll = filtered.reduce((sum, p) => sum + (Number(p.totalHoursAllTime ?? p.totalHours) || 0), 0);
  const sumBrutoAll = filtered.reduce((sum, p) => sum + (Number(p.totalBrutoAllTime ?? p.totalBruto ?? p.totalHours) || 0), 0);
  const brutoAllOf = (p) => (p.totalBrutoAllTime ?? p.totalBruto ?? p.totalHours);
  // Muted "since start" line shown under a period-scoped figure (never without a period).
  const sinceStart = (value, cls = 'text-[10.5px]') => (
    hasPeriod ? <div className={`${cls} text-muted tabular`}>{fmtHrs(value)} since start</div> : null
  );
  const periodLabel = filterYear
    ? [filterMonth ? MONTH_NAMES[parseInt(filterMonth, 10) - 1] : '', filterYear].filter(Boolean).join(' ')
    : 'All projects';
  const isLoading = loading || !loaded;

  // ── Edit handlers ─────────────────────────────────────────────────────────────
  const startEdit = (proj) => setEditing({
    id: proj.id, project: proj.project, client: proj.client,
    newProject: proj.project, newClient: proj.client,
  });

  const cancelEdit = () => setEditing(null);
  const handleChange = (e) => setEditing({ ...editing, [e.target.name]: e.target.value });

  const handleSave = async () => {
    if (projects.some(p =>
      p.project === editing.newProject &&
      p.client  === editing.newClient  &&
      p.id      !== editing.id
    )) { toast.error('This project–client combination already exists.'); return; }

    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oldProject: editing.project, oldClient: editing.client,
          newProject: editing.newProject, newClient: editing.newClient,
        }),
      });
      if (!res.ok) throw new Error('Update failed');
      toast.success('Project updated.');
      setEditing(null);
      fetchProjects();
    } catch { toast.error('Failed to update project'); }
  };

  // ── Delete handler ────────────────────────────────────────────────────────────
  const handleDelete = async (proj) => {
    if (proj.totalHours > 0) {
      toast.error('A project with logged hours cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete "${proj.project}"?\n\nThis cannot be undone.`)) return;
    setDeleting(proj.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${proj.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Delete failed'); }
      toast.success('Project deleted.');
      fetchProjects();
    } catch (err) { toast.error(err.message || 'Failed to delete project'); }
    setDeleting(null);
  };

  // ── Add Project handlers ──────────────────────────────────────────────────────
  const handleAddChange = (e) => setAddForm({ ...addForm, [e.target.name]: e.target.value });

  const handleAddProject = async () => {
    if (!addForm.name.trim()) { toast.error('Project name is required.'); return; }
    if (!addForm.client_id)   { toast.error('Client is required.'); return; }
    setAdding(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to add project'); }
      toast.success('Project added.');
      setShowAdd(false);
      setAddForm({ name: '', client_id: '' });
      fetchProjects();
    } catch (err) { toast.error(err.message || 'Failed to add project'); }
    setAdding(false);
  };

  const closeAddModal = () => { setShowAdd(false); setAddForm({ name: '', client_id: '' }); };

  // Clear returns to the DEFAULT view (current year, no month) — not to "All years".
  const clearFilters = () => {
    setSearch('');
    setPref('projectsFilterClient', '');
    setPref('projectsFilterDept', '');
    setPref('projectsFilterYear', DEFAULT_YEAR);
    setPref('projectsFilterMonth', '');
  };

  const handleFilterChange = (key, value) => {
    if (key === 'search') setSearch(value);
    else if (key === 'client') setPref('projectsFilterClient', value);
    else if (key === 'dept') setPref('projectsFilterDept', value);
    else if (key === 'year') setPref('projectsFilterYear', value); // the month is deliberately kept
    else if (key === 'month') setPref('projectsFilterMonth', value);
  };

  const hasActiveFilters =
    Boolean(search || filterClient || filterDept || filterMonth) || filterYear !== DEFAULT_YEAR;

  // ── Shared class strings ──────────────────────────────────────────────────────
  const thClass =
    'py-3 px-4 border-b border-line text-left text-[10px] font-semibold uppercase ' +
    'tracking-wide text-muted cursor-pointer select-none whitespace-nowrap hover:text-ink';

  const thStatic =
    'py-3 px-4 border-b border-line text-left text-[10px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap';

  const controlClass =
    'text-[12.5px] text-ink bg-surface border border-line rounded-tc-sm px-3 py-2 min-h-[38px] ' +
    'focus:outline-none focus:ring-2 focus:ring-accent';

  const inputClass =
    'w-full text-[13px] text-ink bg-surface border border-line-strong rounded-tc-sm px-3.5 py-2.5 ' +
    'min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent';

  const microLabel = 'block text-[10px] font-semibold uppercase tracking-[0.1em] text-muted mb-1';

  const statusSelect = (proj, extra = '') => {
    const value = normalizeProjectStatus(proj.status);
    return (
      <select
        aria-label={`Status ${proj.project}`}
        value={value}
        onChange={e => handleStatusChange(proj, e.target.value)}
        className={`text-[11.5px] font-semibold rounded-tc-sm px-2.5 py-1.5 border-0 ${STATUS_CLASS[value] || STATUS_CLASS.in_progress} ${extra}`}
      >
        {PROJECT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    );
  };

  const chevron = (d) => (
    <svg {...iconProps}><path d={d} /></svg>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4">
      {/* ── Page header ── */}
      <div className="border-b border-line py-8 mb-6">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent mb-2">{periodLabel}</div>
            <h1 className="font-display text-[46px] leading-none tracking-tight">Projects</h1>
          </div>
          <div className="flex flex-wrap gap-10">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Neto total</div>
              <div className="text-[32px] font-semibold tracking-tight tabular">{fmtHrs(sumNeto)}</div>
              {sinceStart(sumNetoAll)}
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Bruto total</div>
              <div className="text-[32px] font-semibold tracking-tight tabular text-accent">{fmtHrs(sumBruto)}</div>
              {sinceStart(sumBrutoAll)}
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Projects</div>
              <div className="text-[32px] font-semibold tracking-tight tabular">{pad2(filtered.length)}</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            onClick={() => setShowAdd(true)}
            className="bg-accent text-white text-[13px] font-semibold rounded-tc-sm px-4 min-h-[44px]"
          >
            New project
          </button>
        </div>
      </div>

      {/* ── Featured project carousel ── */}
      {filtered.length > 0 && featProj && (
        <section className="mb-6" aria-label="Featured project">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted tabular">
              Featured · {pad2(featIdx + 1)} / {pad2(filtered.length)}
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => stepFeatured(-1)}
                disabled={filtered.length < 2}
                aria-label="Previous project"
                className="inline-flex items-center justify-center w-11 h-11 rounded-tc-sm bg-surface border border-line text-ink disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {chevron('M15 6l-6 6 6 6')}
              </button>
              <button
                type="button"
                onClick={() => stepFeatured(1)}
                disabled={filtered.length < 2}
                aria-label="Next project"
                className="inline-flex items-center justify-center w-11 h-11 rounded-tc-sm bg-ink text-canvas disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {chevron('M9 6l6 6-6 6')}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex-[999_1_520px] min-w-0 bg-surface border border-line rounded-tc-lg px-6 py-5 flex flex-col gap-3.5">
              <div className="flex items-center gap-3">
                <span className={`text-[11.5px] font-semibold rounded-tc-sm px-2.5 py-1.5 ${STATUS_CLASS[normalizeProjectStatus(featProj.status)] || STATUS_CLASS.in_progress}`}>
                  {(PROJECT_STATUSES.find(st => st.value === normalizeProjectStatus(featProj.status)) || PROJECT_STATUSES[0]).label}
                </span>
                <span className="ml-auto text-[11.5px] text-muted">Created {formatMonthYear(featProj.dateCreated)}</span>
              </div>
              <h2 className="font-display text-[22px] leading-tight tracking-tight">{featProj.project}</h2>
              <p className="text-[12.5px] text-muted">{`${featProj.client} · ${featProj.requestedBy || '—'} · ${featProj.department || '—'}`}</p>
              <div className="flex flex-wrap items-end gap-8">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Neto</div>
                  <div className="text-[28px] font-semibold tracking-tight tabular text-ink">{fmtHrs(featProj.totalHours)}</div>
                  {sinceStart(featProj.totalHoursAllTime ?? featProj.totalHours)}
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Bruto</div>
                  <div className="text-[28px] font-semibold tracking-tight tabular text-accent">{fmtHrs(brutoOf(featProj))}</div>
                  {sinceStart(brutoAllOf(featProj))}
                </div>
                <div className="ml-auto">
                  {statusSelect(featProj)}
                  {featProj.status_auto && (
                    <div className="text-[10px] text-muted mt-1" title="No hours logged for 14 days. Log hours or pick another status to reopen.">
                      auto · no hours for 14 days
                    </div>
                  )}
                </div>
              </div>
            </div>
            {peekProj && (
              <div className="flex-[1_1_240px] min-w-0 bg-surface-alt border border-line rounded-tc-lg px-5 py-5 flex flex-col gap-4 opacity-75">
                <div className="text-[10px] font-semibold uppercase tracking-[0.13em] text-muted">Next</div>
                <div className="font-display text-[18px] leading-tight">{peekProj.project}</div>
                <div className="text-[12.5px] text-muted">{`${peekProj.client || '—'} · ${peekProj.requestedBy || '—'}`}</div>
                <div className="mt-auto">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Bruto</div>
                  <div className="text-[22px] font-semibold tabular">{fmtHrs(brutoOf(peekProj))}</div>
                  {sinceStart(brutoAllOf(peekProj))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Search & filter bar ── */}
      <ProjectsFilterBar
        values={{ search, client: filterClient, dept: filterDept, year: filterYear, month: filterMonth }}
        onChange={handleFilterChange}
        years={yearOptions}
        months={MONTH_NAMES}
        clients={uniqueClients}
        departments={uniqueDepts}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        resultCount={filtered.length}
      />

      {/* ── Table ── */}
      <div className="bg-surface border border-line rounded-tc-lg overflow-x-auto">
        <table className="w-full border-collapse text-[12.5px] text-ink">
          <thead className="bg-surface-alt">
            <tr>
              <th aria-sort={sortCol === 'project' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('project')}>
                Project <SortIcon col="project" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th aria-sort={sortCol === 'client' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('client')}>
                Client <SortIcon col="client" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th aria-sort={sortCol === 'requestedBy' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('requestedBy')}>
                Requested by <SortIcon col="requestedBy" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th aria-sort={sortCol === 'department' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('department')}>
                Department <SortIcon col="department" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th aria-sort={sortCol === 'dateCreated' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('dateCreated')}>
                Created <SortIcon col="dateCreated" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th aria-sort={sortCol === 'totalHours' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('totalHours')}>
                Neto <SortIcon col="totalHours" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th aria-sort={sortCol === 'totalBruto' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thClass} onClick={() => handleSort('totalBruto')}>
                Bruto <SortIcon col="totalBruto" sortCol={sortCol} sortDir={sortDir} />
              </th>
              <th className={thStatic}>Status</th>
              <th className={`${thStatic} w-32`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="py-10 text-center text-muted">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="py-10 text-center text-muted">
                {hasPeriod && projects.length === 0 ? 'No activity in the selected period.' : 'No projects match these filters.'}
              </td></tr>
            ) : filtered.map(proj => (
              <tr
                key={proj.id}
                className="border-b border-line hover:bg-surface-alt last:border-0"
              >
                {/* Project Name */}
                <td className="py-2.5 px-4">
                  {editing?.id === proj.id ? (
                    <input name="newProject" aria-label="Project name" value={editing.newProject} onChange={handleChange}
                      className="text-[12.5px] text-ink bg-surface border border-line-strong rounded-tc-sm px-2.5 py-1.5 w-full" />
                  ) : proj.project}
                </td>

                {/* Client */}
                <td className="py-2.5 px-4">
                  {editing?.id === proj.id ? (
                    <input name="newClient" aria-label="Client" value={editing.newClient} onChange={handleChange}
                      className="text-[12.5px] text-ink bg-surface border border-line-strong rounded-tc-sm px-2.5 py-1.5 w-full" />
                  ) : proj.client}
                </td>

                {/* Requested By */}
                <td className="py-2.5 px-4 text-muted">
                  {proj.requestedBy || <span className="text-faint">—</span>}
                </td>

                {/* Department */}
                <td className="py-2.5 px-4 text-muted">
                  {proj.department || <span className="text-faint">—</span>}
                </td>

                {/* Date Created */}
                <td className="py-2.5 px-4 text-muted whitespace-nowrap">
                  {formatMonthYear(proj.dateCreated)}
                </td>

                {/* Neto */}
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tabular text-ink">
                      {typeof proj.totalHours === 'number' ? fmtHrs(proj.totalHours) : '0.0'}
                    </span>
                    <div className="flex-1 bg-line rounded-full h-1.5 min-w-[40px] max-w-[80px]">
                      <div
                        className="h-1.5 rounded-full bg-accent"
                        style={{ width: `${Math.min(100, (Number(proj.totalHours) / maxHours) * 100)}%` }}
                      />
                    </div>
                  </div>
                  {sinceStart(proj.totalHoursAllTime ?? proj.totalHours)}
                </td>

                {/* Bruto */}
                <td className="py-2.5 px-4">
                  <span className="font-semibold tabular text-accent">{fmtHrs(brutoOf(proj))}</span>
                  {sinceStart(brutoAllOf(proj))}
                </td>

                {/* Status */}
                <td className="py-2.5 px-4">
                  {statusSelect(proj)}
                  {proj.status_auto && (
                    <div className="text-[10px] text-muted mt-1" title="No hours logged for 14 days. Log hours or pick another status to reopen.">
                      auto · no hours for 14 days
                    </div>
                  )}
                </td>

                {/* Actions */}
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-1.5">
                    {editing?.id === proj.id ? (
                      <>
                        <button type="button" onClick={handleSave}
                          className="bg-accent text-white text-[12.5px] rounded-tc-sm px-3.5 min-h-[44px]">
                          Save
                        </button>
                        <button type="button" onClick={cancelEdit}
                          className="bg-surface text-ink border border-line text-[12.5px] rounded-tc-sm px-3.5 min-h-[44px]">
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => startEdit(proj)}
                          aria-label="Edit project" title="Edit project"
                          className="inline-flex items-center justify-center w-11 h-11 rounded-tc-sm bg-surface text-muted border border-line">
                          <svg {...iconProps}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(proj)}
                          disabled={deleting === proj.id}
                          aria-label="Delete project"
                          title={proj.totalHours > 0 ? 'Cannot delete — project has logged hours' : 'Delete project'}
                          className="inline-flex items-center justify-center w-11 h-11 rounded-tc-sm bg-surface text-muted hover:text-danger border border-line disabled:opacity-50"
                        >
                          <svg {...iconProps}><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" /></svg>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Add Project modal ── */}
      {showAdd && (
        <div
          className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4"
          onClick={e => { if (e.target === e.currentTarget) closeAddModal(); }}
        >
          <div className="bg-surface border border-line rounded-tc-xl p-7 w-full max-w-md" role="dialog" aria-modal="true" aria-labelledby="add-project-title">
            <h2 id="add-project-title" className="font-display text-[26px] leading-tight mb-5">New project</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="add-project-name" className={microLabel}>
                  Project name <span className="text-danger">*</span>
                </label>
                <input
                  id="add-project-name"
                  name="name" value={addForm.name} onChange={handleAddChange}
                  placeholder="Enter project name"
                  className={inputClass} autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleAddProject()}
                />
              </div>
              <div>
                <label htmlFor="add-project-client" className={microLabel}>
                  Client <span className="text-danger">*</span>
                </label>
                <select id="add-project-client" name="client_id" value={addForm.client_id} onChange={handleAddChange} className={inputClass}>
                  <option value="">Select client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={closeAddModal}
                className="bg-surface text-muted border border-line text-sm rounded-tc-sm px-5 min-h-[44px]">
                Cancel
              </button>
              <button type="button" onClick={handleAddProject} disabled={adding}
                className="bg-accent text-white text-sm font-semibold rounded-tc-sm px-5 min-h-[44px] disabled:opacity-60">
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
